import type { CyanGoalPlan, CyanGoalStep, GoalAuthority } from "./types";

export const CYAN_AUTONOMY_PRINCIPLES = [
  "Owner intent is the root goal authority.",
  "Canonical SPEC remains supreme project truth.",
  "Cyan must understand before planning.",
  "Cyan must plan before executing.",
  "Cyan must discover available capabilities before selecting tools or workers.",
  "Cyan must use the least authority required for each step.",
  "Cyan must distinguish observation, planning, preparation, reversible execution, and privileged execution.",
  "Cyan must respect approval gates.",
  "Cyan must not convert ambiguity into destructive action.",
  "Cyan must preserve evidence for consequential actions.",
  "Cyan must verify execution results rather than assume success.",
  "Cyan may repair or retry only within granted authority.",
  "Cyan must escalate when authority, safety, truth, or required capability is missing.",
  "Cyan must never fabricate execution, verification, credentials, state, memory, or results.",
  "Cyan must report completed, blocked, failed, and approval-required states accurately.",
] as const;

const APPROVAL_REQUIRED_AUTHORITIES: ReadonlySet<GoalAuthority> = new Set([
  "execute_privileged",
]);

export function stepRequiresOwnerApproval(step: CyanGoalStep): boolean {
  if (APPROVAL_REQUIRED_AUTHORITIES.has(step.authority)) {
    return true;
  }

  if (step.risk === "critical") {
    return true;
  }

  if (step.risk === "high" && !step.reversible) {
    return true;
  }

  return false;
}

export function evaluateGoalPlanApproval(
  plan: Omit<CyanGoalPlan, "requiresOwnerApproval" | "approvalReasons">,
): Pick<CyanGoalPlan, "requiresOwnerApproval" | "approvalReasons"> {
  const reasons = plan.steps
    .filter(stepRequiresOwnerApproval)
    .map((step) => `${step.id}:${step.authority}:${step.risk}`);

  return {
    requiresOwnerApproval: reasons.length > 0,
    approvalReasons: reasons,
  };
}
